import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { playCoverChime } from "@/lib/audio";
import {
    chooseHabitToOffer, pickCoverOfferPhrase, pickCoverAcceptPhrase, pickCoverDeclinePhrase,
} from "@/lib/coverPass";
import { TODAY } from "@/lib/dates";

// Cover-pass flow: Mochi offers to "cover" an at-risk habit streak once a week.
//   coverOffer.habit:  the at-risk habit Mochi is offering to cover (null = no offer)
//   coverOffer.message: the rotating offer phrase, pre-filled with the habit title
//   Dismissals are localStorage-backed so we don't re-offer the same habit
//   on every render after the user has answered today.
export function useCoverPassFlow({ mood, handleCoverHabit, todaysHabits, settings }) {
    const [coverOffer, setCoverOffer] = useState({ open: false, habit: null, message: "" });
    const dismissedRef = useRef(null);

    // Restore "already dismissed today" from localStorage on mount so a refresh
    // doesn't immediately re-offer the same pass the user just said no to.
    useEffect(() => {
        try {
            const raw = window.localStorage.getItem("mochi-cover-dismissed");
            if (raw) dismissedRef.current = JSON.parse(raw);
        } catch (e) {
            console.warn("cover-pass: failed to restore dismissal state", e);
        }
    }, []);

    const persistDismissal = useCallback((habitId) => {
        const today = TODAY();
        dismissedRef.current = { date: today, habitId };
        try {
            window.localStorage.setItem("mochi-cover-dismissed", JSON.stringify({ date: today, habitId }));
        } catch (e) {
            console.warn("cover-pass: failed to persist dismissal state", e);
        }
    }, []);

    // Acceptance: triggers wink + soft chime + ⭐ on the row + accept-toast.
    // Pass is consumed server-side; local settings update is handled in useItems.
    const handleAcceptCoverPass = useCallback(async () => {
        const target = coverOffer.habit;
        if (!target) return;
        setCoverOffer((c) => ({ ...c, open: false }));
        try {
            const res = await handleCoverHabit(target);
            mood.forceMoodFor?.("wink", 1300);
            playCoverChime();
            mood.setBubbleMsg?.(pickCoverAcceptPhrase(res?.habit?.title || target.title));
            toast("⭐ mochi covered this one 🍡", { duration: 4500 });
        } catch (e) {
            console.warn("cover-pass: accept failed", e);
            toast("hmm — pass not available right now", { duration: 3000 });
        }
    }, [coverOffer.habit, handleCoverHabit, mood]);

    const handleDeclineCoverPass = useCallback(() => {
        const target = coverOffer.habit;
        setCoverOffer({ open: false, habit: null, message: "" });
        if (target?.id) persistDismissal(target.id);
        mood.setBubbleMsg?.(pickCoverDeclinePhrase());
    }, [coverOffer.habit, mood, persistDismissal]);

    // Manual entry from a habit row's pencil menu — opens the same offer card.
    const askMochiToCover = useCallback((habit) => {
        if (!habit) return;
        setCoverOffer({
            open: true,
            habit,
            message: pickCoverOfferPhrase(habit.title),
        });
    }, []);

    // Proactive offer (Situation 1): once per render-batch, check if conditions
    // are met (after 8pm EST + at-risk habit + pass available + not dismissed
    // already today + no other offer open).
    useEffect(() => {
        if (coverOffer.open) return;
        if (!settings) return;
        const candidate = chooseHabitToOffer(todaysHabits, settings);
        if (!candidate) return;
        const todayKey = TODAY();
        const dismissed = dismissedRef.current;
        if (dismissed && dismissed.date === todayKey && dismissed.habitId === candidate.id) return;
        setCoverOffer({
            open: true,
            habit: candidate,
            message: pickCoverOfferPhrase(candidate.title),
        });
    }, [todaysHabits, settings, coverOffer.open]);

    return { coverOffer, handleAcceptCoverPass, handleDeclineCoverPass, askMochiToCover };
}
